import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Heart, Users, BookOpen, Star, Facebook, Instagram, Mail, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from './ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoodleBugLanding = () => {
  const [books, setBooks] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: '',
    type: 'general'
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Fetch books and testimonials on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch books
        const booksResponse = await fetch(`${API}/books`);
        const booksData = await booksResponse.json();
        
        // Fetch testimonials  
        const testimonialsResponse = await fetch(`${API}/testimonials`);
        const testimonialsData = await testimonialsResponse.json();
        
        setBooks(booksData.books || []);
        setTestimonials(testimonialsData.testimonials || []);
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load content. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      toast.error('Please fill in all required fields.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success('Thank you for your message! We will respond within 24-48 hours.');
        setContactForm({ name: '', email: '', message: '', type: 'general' });
      } else {
        toast.error('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle newsletter subscription
  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error('Please enter your email address.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API}/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newsletterEmail }),
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message);
        setNewsletterEmail('');
      } else {
        toast.error('Subscription failed. Please try again.');
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      toast.error('Subscription failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-lg text-gray-600 font-playful">Loading Doodle Bug...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-blue-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_bilingual-bugs/artifacts/f8n6mani_Doodle%20Bug%20Colouring%20logo.png" 
            alt="Doodle Bug Logo" 
            className="h-24 w-auto"
          />
          <div className="bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 via-purple-400 to-pink-400 text-white font-bold text-lg py-3 px-6 rounded-full shadow-lg text-center transform hover:scale-105 transition-all duration-300">
            🌈 Bringing Colour to Learning! 🌈
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 text-center overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '0.5s' }}></div>
        <div className="absolute top-20 right-20 w-16 h-16 bg-blue-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-20 w-12 h-12 bg-green-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '1.5s' }}></div>
        <div className="absolute bottom-10 right-10 w-24 h-24 bg-red-300 rounded-full opacity-60 animate-bounce" style={{ animationDelay: '2s' }}></div>
        
        <div className="container mx-auto max-w-4xl relative z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_bilingual-bugs/artifacts/f8n6mani_Doodle%20Bug%20Colouring%20logo.png" 
            alt="Doodle Bug Logo" 
            className="h-72 w-auto mx-auto mb-8 transform hover:scale-105 transition-transform duration-300"
          />
          <h1 className="text-5xl md:text-6xl font-bold text-blue-600 mb-6 font-rounded">
            Learning Languages Through
            <span className="block text-red-500">Creativity and Colour</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8 font-medium">
            Bilingual colouring books that make learning fun for children ages 3-8
          </p>
          <Button 
            onClick={() => {
              const booksSection = document.getElementById('books-section');
              if (booksSection) {
                booksSection.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-green-500 hover:bg-green-600 text-white text-xl font-bold px-8 py-4 rounded-full shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Shop Our Books on Amazon
          </Button>
        </div>
      </section>

      {/* About Doodle Bug Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-blue-600 mb-8 font-rounded">About Doodle Bug</h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            At Doodle Bug, we believe in the power of colouring to inspire creativity, language learning, and kindness. 
            Each page combines fun illustrations with bilingual phrases to help children learn and grow while developing 
            positive social skills and cultural awareness.
          </p>
        </div>
      </section>

      {/* Our Books Section */}
      <section id="books-section" className="py-16 px-4 bg-gradient-to-r from-yellow-50 to-green-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-red-500 mb-12 font-rounded">Our Books</h2>
          
          {/* Filter to get only Good Friend books (not coming soon) */}
          {(() => {
            const goodFriendBooks = books.filter(book => 
              book.title === "How to be a Good Friend" && !book.comingSoon
            );
            
            if (goodFriendBooks.length === 0) return null;
            
            return (
              <div className="max-w-5xl mx-auto">
                {/* Book Cards Row */}
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {goodFriendBooks.map((book, index) => (
                    <Card key={book.id || index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:scale-105">
                      <CardContent className="p-6">
                        <div className="h-80 rounded-lg mb-4 bg-white p-2 flex items-center justify-center border-2 border-gray-100">
                          {book.coverImage && book.coverImage.startsWith('http') ? (
                            <img 
                              src={book.coverImage} 
                              alt={`${book.title} cover`}
                              className="max-w-full max-h-full object-contain rounded-lg hover:scale-105 transition-transform duration-300 shadow-md"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <div className="text-center">
                              <BookOpen className="w-16 h-16 text-blue-500 mx-auto mb-2" />
                              <p className="text-sm text-gray-600">Book Cover Preview</p>
                            </div>
                          )}
                          <div className="text-center hidden">
                            <BookOpen className="w-16 h-16 text-blue-500 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Book Cover Preview</p>
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-blue-600 mb-2 font-rounded text-center">{book.title}</h3>
                        <p className="text-lg font-semibold text-red-500 mb-4 text-center">{book.languages}</p>
                        <Button 
                          data-testid={`amazon-btn-${book.id}`}
                          className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-4 rounded-full shadow-md transform hover:scale-105 transition-all duration-200"
                          onClick={() => window.open(book.amazonLink, '_blank')}
                        >
                          View on Amazon
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Shared Description Box */}
                <Card className="bg-white rounded-xl shadow-xl">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <h3 className="text-3xl font-bold text-blue-600 mb-4 font-rounded">🌈 How to Be a Good Friend 🌈</h3>
                      <div 
                        className="text-lg leading-relaxed max-w-4xl mx-auto"
                        style={{lineHeight: '1.6'}}
                        dangerouslySetInnerHTML={{
                          __html: `<p class="text-xl font-bold text-red-600" style="margin: 0; padding: 0; margin-bottom: 0.5rem;"><strong>Hug, Share, Care!</strong> A Heartwarming Colouring Adventure for Little Ones Ages 4–6</p>
                          <div class="text-gray-700" style="margin: 0; padding: 0; margin-top: -0.5rem;">
                          ${goodFriendBooks[0]?.description
                            ?.replace(/🌈 How to Be a Good Friend 🌈/g, '')
                            ?.replace(/\*\*Hug, Share, Care!\*\* A Heartwarming Colouring Adventure for Little Ones Ages 4–6/g, '')
                            ?.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-900">$1</strong>')
                            ?.replace(/Hug, Share, Care!/g, '<strong class="font-bold text-red-600">Hug, Share, Care!</strong>')
                            ?.replace(/\n\n/g, '<br/><br/>')
                            ?.replace(/\n/g, '<br/>')
                            ?.replace(/^(<br\/?>)+/i, '') // Remove leading <br> tags
                            ?.trim() || ''}
                          </div>`
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* How to Help the Planet Books */}
          {(() => {
            const planetBooks = books.filter(book => 
              book.title === "How to Help the Planet" && !book.comingSoon
            );
            
            if (planetBooks.length === 0) return null;
            
            return (
              <div className="max-w-5xl mx-auto mt-16">
                {/* Book Cards Row */}
                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  {planetBooks.map((book, index) => (
                    <Card key={book.id || index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:scale-105">
                      <CardContent className="p-6">
                        <div className="h-80 rounded-lg mb-4 bg-white p-2 flex items-center justify-center border-2 border-gray-100">
                          {book.coverImage && book.coverImage.startsWith('http') ? (
                            <img 
                              src={book.coverImage} 
                              alt={`${book.title} cover`}
                              className="max-w-full max-h-full object-contain rounded-lg hover:scale-105 transition-transform duration-300 shadow-md"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <div className="text-center">
                              <BookOpen className="w-16 h-16 text-green-500 mx-auto mb-2" />
                              <p className="text-sm text-gray-600">Book Cover Preview</p>
                            </div>
                          )}
                          <div className="text-center hidden">
                            <BookOpen className="w-16 h-16 text-green-500 mx-auto mb-2" />
                            <p className="text-sm text-gray-600">Book Cover Preview</p>
                          </div>
                        </div>
                        <h3 className="text-2xl font-bold text-green-600 mb-2 font-rounded text-center">{book.title}</h3>
                        <p className="text-lg font-semibold text-red-500 mb-4 text-center">{book.languages}</p>
                        <Button 
                          data-testid={`amazon-btn-${book.id}`}
                          className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 px-4 rounded-full shadow-md transform hover:scale-105 transition-all duration-200"
                          onClick={() => window.open(book.amazonLink, '_blank')}
                        >
                          View on Amazon
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Shared Description Box */}
                <Card className="bg-white rounded-xl shadow-xl">
                  <CardContent className="p-8">
                    <div className="text-center">
                      <h3 className="text-3xl font-bold text-green-600 mb-4 font-rounded">How to Help the Planet</h3>
                      <div 
                        className="text-lg leading-relaxed max-w-4xl mx-auto"
                        style={{lineHeight: '1.6'}}
                        dangerouslySetInnerHTML={{
                          __html: `<p class="text-xl font-bold text-green-600" style="margin: 0; padding: 0; margin-bottom: 0.5rem;"><strong>Reduce, Reuse, Recycle!</strong> An Eco-Friendly Colouring Adventure for Little Ones Ages 4-6</p>
                          <div class="text-gray-700" style="margin: 0; padding: 0; margin-top: -0.5rem;">
                          ${planetBooks[0]?.description
                            ?.replace(/🌍 How to Help the Planet 🌍/g, '')
                            ?.replace(/\*\*Reduce, Reuse, Recycle!\*\* An Eco-Friendly Colouring Adventure for Little Ones Ages 4–6/g, '')
                            ?.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-900">$1</strong>')
                            ?.replace(/Reduce, Reuse, Recycle!/g, '<strong class="font-bold text-green-600">Reduce, Reuse, Recycle!</strong>')
                            ?.replace(/\n\n/g, '<br/><br/>')
                            ?.replace(/\n/g, '<br/>')
                            ?.replace(/^(<br\/?>)+/i, '')
                            ?.trim() || ''}
                          </div>`
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Learning with Love Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-green-500 mb-12 font-rounded">Learning with Love</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gradient-to-b from-blue-50 to-blue-100 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="bg-blue-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-blue-600 mb-2 font-rounded">Bilingual Learning Made Fun</h3>
              <p className="text-gray-700">Interactive pages that make language learning an exciting adventure</p>
            </div>
            <div className="text-center p-6 bg-gradient-to-b from-red-50 to-red-100 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="bg-red-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-red-600 mb-2 font-rounded">Encourages Positive Social Skills</h3>
              <p className="text-gray-700">Teaching kindness, sharing, and friendship through engaging stories</p>
            </div>
            <div className="text-center p-6 bg-gradient-to-b from-green-50 to-green-100 rounded-xl shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="bg-green-500 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-green-600 mb-2 font-rounded">Perfect for Classrooms or Home Learning</h3>
              <p className="text-gray-700">Designed for both teachers and parents to create meaningful learning moments</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-50 to-pink-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center text-purple-600 mb-12 font-rounded">What Parents & Teachers Say</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={testimonial.id || index} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic">"{testimonial.review}"</p>
                  <div className="flex items-center">
                    <div className="bg-gradient-to-r from-blue-400 to-green-400 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold mr-3">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-2xl">
          <h2 className="text-4xl font-bold text-center text-blue-600 mb-8 font-rounded">Get in Touch</h2>
          <Card className="shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                    <Input
                      type="text"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <Textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    placeholder="Tell us how we can help you!"
                    rows={4}
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Stay Connected Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-50 to-green-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-blue-600 mb-6 font-rounded">Stay Connected</h2>
          <p className="text-lg text-gray-700 mb-8">
            Follow Doodle Bug for colouring ideas, classroom tips, and new book releases!
          </p>
          
          {/* Social Media Links */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center"
              onClick={() => window.open('https://www.facebook.com/profile.php?id=61579950123238', '_blank')}
            >
              <Facebook className="w-5 h-5 mr-2" />
              Follow us on Facebook
            </Button>
            <Button 
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold px-6 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center"
              onClick={() => window.open('https://www.instagram.com/doodlebugcolours/', '_blank')}
            >
              <Instagram className="w-5 h-5 mr-2" />
              Follow us on Instagram
            </Button>
          </div>
        </div>
      </section>

      {/* Free Colouring Pages Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-pink-50 to-yellow-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-purple-600 mb-6 font-rounded">🎨 Free Sample Pages! 🎨</h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Try before you buy! Download these free "How are you doing?" colouring pages 
              and see why children love learning with Doodle Bug.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* French Sample */}
            <Card className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:scale-105">
              <CardContent className="p-6">
                <div className="h-80 rounded-lg mb-4 bg-white p-2 flex items-center justify-center border-2 border-gray-100 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_bilingual-bugs/artifacts/uznkbgjs_french%20how%20are%20you%20doing%20page.png" 
                    alt="French How Are You Doing Sample Page"
                    className="max-w-full max-h-full object-contain rounded-lg shadow-md"
                  />
                </div>
                <h3 className="text-2xl font-bold text-blue-600 mb-2 font-rounded">French Sample Page</h3>
                <p className="text-lg font-semibold text-red-500 mb-3">English + Français</p>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "How are you doing?" - Learn greetings in French while colouring! 
                  Perfect for practicing everyday conversations with beautiful illustrations.
                </p>
                <Button 
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-4 rounded-full shadow-md transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                  onClick={() => window.open('https://customer-assets.emergentagent.com/job_bilingual-bugs/artifacts/uznkbgjs_french%20how%20are%20you%20doing%20page.png', '_blank')}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download French Page
                </Button>
              </CardContent>
            </Card>

            {/* Spanish Sample */}
            <Card className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 transform hover:scale-105">
              <CardContent className="p-6">
                <div className="h-80 rounded-lg mb-4 bg-white p-2 flex items-center justify-center border-2 border-gray-100 overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_bilingual-bugs/artifacts/edjmppl0_how%20are%20you%20doing%20_%20spanish.png" 
                    alt="Spanish How Are You Doing Sample Page"
                    className="max-w-full max-h-full object-contain rounded-lg shadow-md"
                  />
                </div>
                <h3 className="text-2xl font-bold text-blue-600 mb-2 font-rounded">Spanish Sample Page</h3>
                <p className="text-lg font-semibold text-red-500 mb-3">English + Español</p>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  "How are you doing?" - Learn greetings in Spanish while colouring! 
                  Perfect for practicing everyday conversations with beautiful illustrations.
                </p>
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-full shadow-md transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                  onClick={() => window.open('https://customer-assets.emergentagent.com/job_bilingual-bugs/artifacts/edjmppl0_how%20are%20you%20doing%20_%20spanish.png', '_blank')}
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Spanish Page
                </Button>
              </CardContent>
            </Card>
          </div>
          
          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-lg text-gray-700 mb-6">
              Love these samples? Get the complete books with 70+ pages of learning fun!
            </p>
            <Button 
              onClick={() => {
                const booksSection = document.getElementById('books-section');
                if (booksSection) {
                  booksSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-green-500 hover:bg-green-600 text-white text-xl font-bold px-8 py-4 rounded-full shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Shop Full Books Now
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 px-4 border-t-2 border-gray-100">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_bilingual-bugs/artifacts/f8n6mani_Doodle%20Bug%20Colouring%20logo.png" 
              alt="Doodle Bug Logo" 
              className="h-32 w-auto mx-auto mb-8"
            />
            
            {/* Rainbow Banner */}
            <div className="relative mb-8 mx-auto max-w-2xl">
              <div className="bg-gradient-to-r from-red-400 via-yellow-400 via-green-400 via-blue-400 via-purple-400 to-pink-400 text-white font-bold text-2xl md:text-3xl py-6 px-8 rounded-full shadow-xl text-center transform hover:scale-105 transition-all duration-300">
                🌈 Bringing Colour to Learning! 🌈
              </div>
            </div>
            
            <Separator className="my-8 bg-gray-300" />
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-600 mb-4 md:mb-0">© 2025 Doodle Bug Colouring Books</p>
              <div className="flex items-center text-gray-600">
                <Mail className="w-4 h-4 mr-2" />
                <span>DoodleBugcolours@gmail.com</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DoodleBugLanding;